// @flow

import React, {Component} from 'react';
import _ from 'lodash';

import I18n from 'PMT/i18n/I18n';

import Listener from 'PMT/flux/Listener';

// -- constants
import Events from 'constants/Events';
import RoutingEnum from 'constants/RoutingEnum';
import Config from 'constants/Config';

// -- actions
import RouteAction from 'actions/RouteAction';
import RestaurantMenuAction from 'actions/RestaurantMenuAction';
import OrderAction from 'actions/OrderAction';

// -- stores
import {
  RestaurantStore,
  SessionStore,
  RestaurantMenuStore,
  OrderSettingsStore
} from 'stores';

// -- entities
import Restaurant from 'PMT/entities/Restaurant';
import RestaurantMenu from 'PMT/entities/menu/RestaurantMenu';

// -- components
import AbstractComponent from 'components/AbstractComponent';
import Ariane from 'PMT/components/Ariane';
import CategoriesListPage from './categories/CategoriesListPage';
import UpsellingCategoriesListPage
  from './upsellingCategories/list/UpsellingCategoriesListPage';
import SuggestionsListPage from './suggestions/SuggestionsListPage';
import MenusListPage from './menus/MenusListPage';
import OptionsListPage from './options/OptionsListPage';
import ProductsListPage from './products/ProductsListPage';
import SynchronizeMenuView from './components/SynchronizeMenuView';
import {Tabs, Tab} from 'react-bootstrap';
import RestaurantMenuTabs from "pages/menu/RestaurantMenuTabs";

export default class RestaurantMenuPage extends AbstractComponent {

  arianeIcon = 'fa-shopping-cart';

  arianeDetails = [
    {
      name: I18n.tr('menu.order'),
      link: null
    },
    {
      name: I18n.tr('menu.order.restaurant_menu'),
      link: null
    }
  ];

  state = {
    restaurantMenu: RestaurantMenuStore.getRestaurantMenu(),
    restaurant: RestaurantStore.getRestaurant(),

    orderSettings: OrderSettingsStore.getOrderSettings(),

    apiError: null,
    loading: false,
  };

  componentDidMount() {
    if (!RestaurantStore.getRestaurant().haveFeatureOrder()) {
      RouteAction.redirectToFeatureNonActivated();
      return;
    }

    RestaurantMenuStore.addListeners(this.restaurantMenuStoreListeners);
    OrderSettingsStore.addListeners(this.orderSettingsStoreListeners);

    this.showLoading();
    RestaurantMenuAction.loadRestaurantMenu();

    if (_.isNull(OrderSettingsStore.getOrderSettings())) {
      OrderAction.loadOrderSettings();
    }
  }

  componentWillUnmount() {
    RestaurantMenuStore.removeListeners(this.restaurantMenuStoreListeners);
    OrderSettingsStore.removeListeners(this.orderSettingsStoreListeners);
  }

  get restaurantMenuStoreListeners(): Array<Listener> {
    return [
      new Listener(Events.ON_LOADING_RESTAURANT_MENU_SUCCESS, this.onChange),
      new Listener(Events.ON_LOADING_RESTAURANT_MENU_ERROR, this.onError),
      new Listener(Events.ON_UPDATE_RESTAURANT_MENU_SUCCESS, this.onChange),
      new Listener(Events.ON_UPDATE_RESTAURANT_MENU_ERROR, this.onError),
    ];
  }

  get orderSettingsStoreListeners(): Array<Listener> {
    return [
      new Listener(Events.ON_LOAD_ORDER_SETTINGS_SUCCESS,
          this.onOrderSettingsChange),
      new Listener(Events.ON_UPDATE_ORDER_SETTINGS_SUCCESS,
          this.onOrderSettingsChange),
      new Listener(Events.ON_UPDATE_ORDER_SETTINGS_FAILURE, this.onError),
    ];
  }

  // -- Event functions

  setLoading = () => {
    this.setState({
      loading: true
    });
  }

  onChange = () => {
    this.setState({
      restaurantMenu: RestaurantMenuStore.getRestaurantMenu().copy(),
      loading: false
    });
  };

  onError = () => {
    this.setState({
      apiError: RestaurantMenuStore.getApiError(),
      loading: false
    });
  };

  onOrderSettingsChange = () => {
    this.setState({
      orderSettings: OrderSettingsStore.getOrderSettings(),
    });
  };

  // -- Tabs

  onTabChange = (activeKey) => {
    if (!_.isUndefined(activeKey)) {
      SessionStore.saveRestaurantMenuTab(activeKey);

      /*
       * Update the query params.
       */
      this.onChangeQueryUrl({
        tab: activeKey
      });
    }
  };

  get defaultTabKey(): string {
    if (!_.isEmpty(this.props.location.search)) {
      const params = new URLSearchParams(this.props.location.search);
      const tabQuery = params.get('tab');
      if (tabQuery) {
        return tabQuery;
      }
    }

    // behaviour before node14->16 migration: the selected tab was not restored (because of a bug)
    // bug is now fixed, but to keep the same behaviour
    // we return the default tab instead of the one from session storage
    //  return SessionStore.getRestaurantMenuTab();
    return RestaurantMenuTabs.CATEGORIES;
  }

  // -- renderers

  renderBody() {
    return (
        <Tabs
            defaultActiveKey={this.defaultTabKey}
            id="restaurant_menu__tabs"
            onSelect={this.onTabChange}
        >

          <Tab
              eventKey={RestaurantMenuTabs.CATEGORIES}
              title={I18n.tr('menu.tabs.categories').toUpperCase()}
          >
            <div className="top-buffer-30">
              <CategoriesListPage
                  restaurantMenu={this.state.restaurantMenu}
              />
            </div>
          </Tab>

          <Tab
              eventKey={RestaurantMenuTabs.UPSELLING_CATEGORIES}
              title={I18n.tr('menu.tabs.upselling_categories').toUpperCase()}
          >
            <div className="top-buffer-30">
              <UpsellingCategoriesListPage
                  restaurantMenu={this.state.restaurantMenu}
              />
            </div>
          </Tab>

          <Tab
              eventKey={RestaurantMenuTabs.SUGGESTIONS}
              title={I18n.tr('menu.tabs.suggestions').toUpperCase()}
          >
            <div className="top-buffer-30">
              <SuggestionsListPage
                  restaurantMenu={this.state.restaurantMenu}
              />
            </div>
          </Tab>

          <Tab
              eventKey={RestaurantMenuTabs.PRODUCTS}
              title={I18n.tr('menu.tabs.products').toUpperCase()}
          >
            <div className="top-buffer-30">
              <ProductsListPage
                  restaurantMenu={this.state.restaurantMenu}
                  disableStockModification={this.state.orderSettings.menuManagedByPos}
                  currencyCode={this.state.restaurant.currency}
              />
            </div>
          </Tab>

          <Tab
              eventKey={RestaurantMenuTabs.MENUS}
              title={I18n.tr('menu.tabs.menus').toUpperCase()}
          >
            <div className="top-buffer-30">
              <MenusListPage
                  restaurantMenu={this.state.restaurantMenu}
                  currencyCode={this.state.restaurant.currency}
              />
            </div>
          </Tab>
          <Tab
              eventKey={RestaurantMenuTabs.OPTIONS}
              title={I18n.tr('menu.tabs.options').toUpperCase()}
          >
            <div className="top-buffer-30">
              <OptionsListPage
                  restaurantMenu={this.state.restaurantMenu}
              />
            </div>
          </Tab>
        </Tabs>
    );
  }

  renderFeatureOrderNotEnabled() {
    // TODO: renderFeatureOrderNotEnabled
    return (null);
  }

  render() {
    const restaurant: Restaurant = RestaurantStore.getRestaurant();
    const restaurantMenu: RestaurantMenu = this.state.restaurantMenu;

    let contentView = (null);

    if (!restaurant.haveFeatureOrder()) {
      contentView = this.renderFeatureOrderNotEnabled();
    } else if (_.isNull(restaurantMenu) && _.isNull(this.state.apiError)) { // is loading
      contentView = this.renderOnLoadingContent();
    } else if (_.isNull(restaurantMenu) && !_.isNull(this.state.apiError)) { // error at loading
      // a menu not yet existing (eg. when the store has just been created, and the sync has never been made) should not be displayed as an error
      const displayAsError = this.state.apiError.code !== "404"
      return (
          <div>
            <div>
              {!_.isNull(this.state.orderSettings)
                  && this.state.orderSettings.menuManagedByPos &&
                  <SynchronizeMenuView restaurantMenu={restaurantMenu}
                                       apiError={this.state.apiError}
                                       isLoading={this.state.loading}
                                       setLoading={this.setLoading}/>

              }
            </div>

            <div style={{width: 360}}>
              {displayAsError ? this.renderApiErrorBlock() : (
                  <div style={{
                    padding: "5px 10px",
                    margin: "15px",
                    fontSize: "13pt",
                    textAlign: "center"
                  }} className={"border-main-color"}>
                    {this.state.apiError.localizedMessage
                        || this.state.apiError.message}
                  </div>
              )}
            </div>
          </div>
      )
    } else {
      contentView = this.renderBody();
    }

    return (
        <div className="restaurant_menu">

          <Ariane icon={this.arianeIcon} details={this.arianeDetails}/>

          {!_.isNull(this.state.orderSettings)
              && this.state.orderSettings.menuManagedByPos &&
              <SynchronizeMenuView restaurantMenu={restaurantMenu}
                                   apiError={this.state.apiError}
                                   isLoading={this.state.loading}
                                   setLoading={this.setLoading}/>
          }

          {this.renderLoading()}

          {contentView}
        </div>
    );
  }

}
